/**
 * tone. newsletter — client half.
 *
 * Progressive enhancement over the existing `{% form 'customer' %}`. With JS off
 * the form still submits natively to Shopify and still creates a customer, so
 * the signup is never dependent on this file loading. With JS on we intercept,
 * validate locally, and POST to the app proxy — which is the path that records
 * express marketing consent and returns the discount code.
 *
 * Client validation exists to save a round trip on obvious typos, NOT to secure
 * anything. Every rule here is re-run on the server, which additionally checks
 * that the domain can receive mail — something a browser cannot do.
 */
(function () {
  'use strict';

  // Kept identical to EMAIL_RE in src/email.js. A test asserts they match; if
  // you edit one, edit both.
  const EMAIL_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

  const ENDPOINT = '/apps/newsletter/subscribe';
  const MAX_TOTAL = 254;
  const MAX_LOCAL = 64;

  function localError(raw) {
    const email = (raw || '').trim().toLowerCase();
    if (!email) return 'Please enter your email address.';
    if (email.length > MAX_TOTAL) return 'That address is longer than email addresses are allowed to be.';
    const at = email.lastIndexOf('@');
    if (at < 1) return "That doesn't look like an email address — check for a typo.";
    if (email.slice(0, at).length > MAX_LOCAL) return 'That address is longer than email addresses are allowed to be.';
    if (email.indexOf('..') !== -1 || !EMAIL_RE.test(email)) {
      return "That doesn't look like an email address — check for a typo.";
    }
    return null;
  }

  // The discount code lives in localStorage, NOT in the success cookie: that
  // cookie is contractually a boolean success flag and nothing else. A promo
  // code isn't personal data, but widening the cookie's contents is not
  // something to do quietly.
  const CODE_KEY = 'tone_newsletter_code';

  function rememberCode(code) {
    try { if (code) window.localStorage.setItem(CODE_KEY, code); } catch (e) { /* private mode */ }
  }

  function recallCode() {
    try { return window.localStorage.getItem(CODE_KEY); } catch (e) { return null; }
  }

  function init(root) {
    const form = root.querySelector('form');
    const input = root.querySelector('input[type="email"]');
    const button = root.querySelector('button[type="submit"], button[name="commit"]');
    const success = root.querySelector('[data-tone-newsletter-success]');
    const fields = root.querySelector('[data-tone-newsletter-fields]');
    if (!form || !input || !button) return;

    // Only take over once we know we can: if fetch is missing, leave the native
    // form submission completely alone.
    if (typeof window.fetch !== 'function') return;

    let errorEl = root.querySelector('[data-tone-newsletter-error]');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'tone-footer__error';
      errorEl.setAttribute('data-tone-newsletter-error', '');
      errorEl.setAttribute('role', 'alert');
      errorEl.hidden = true;
      (fields || form).insertAdjacentElement('afterend', errorEl);
    }

    const errorId = errorEl.id || 'tone-newsletter-error';
    errorEl.id = errorId;

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorId);
      // Move focus back to the field so a screen-reader user lands on the thing
      // that needs correcting — but never scroll to do it. The footer is at the
      // bottom of a long page and a focus-driven jump is disorienting.
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }

    function showSuccess(payload) {
      if (fields) fields.hidden = true;
      clearError();
      if (success) {
        let msg = success.getAttribute('data-default-message') || success.textContent;
        const code = (payload && payload.discountCode) || recallCode();
        if (code) {
          rememberCode(code);
          msg = "You're on the list. Use code " + code + ' for 10% off your first bag.';
        } else if (payload && payload.alreadySubscribed) {
          msg = "You're already on the list — nothing more to do.";
        }
        success.textContent = msg;
        success.hidden = false;
        try { success.focus({ preventScroll: true }); } catch (e) { /* older engines */ }
      }
      root.setAttribute('data-newsletter-complete', 'true');
    }

    // Native constraint bubbles duplicate our inline message; suppress them and
    // own the messaging in one place.
    form.setAttribute('novalidate', 'novalidate');

    input.addEventListener('input', function () {
      if (!errorEl.hidden) clearError();
    });

    form.addEventListener('submit', function (event) {
      // FIRST, unconditionally, before any logic that could throw.
      //
      // This used to run after localError(), which meant any exception in
      // validation let the browser fall through to a native POST to /contact —
      // a full page reload that lands on Shopify's generic
      // `posted_successfully` message and loses the discount code entirely.
      // Nothing below this line is allowed to resurrect that path.
      event.preventDefault();

      let problem;
      try {
        problem = localError(input.value);
      } catch (e) {
        problem = "That doesn't look like an email address — check for a typo.";
      }
      if (problem) {
        showError(problem);
        return;
      }

      clearError();

      const original = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Signing you up…';

      const body = new URLSearchParams();
      body.set('email', input.value.trim());
      body.set('source', root.getAttribute('data-newsletter-source') || 'storefront_footer');
      const honeypot = root.querySelector('[data-tone-newsletter-hp]');
      if (honeypot) body.set('website', honeypot.value || '');

      fetch(form.getAttribute('action') || ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          // Both matter: the server forks on these to decide JSON vs a 303
          // redirect, because the same endpoint also serves the no-JS form post.
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
      })
        .then(function (res) {
          // The App Proxy does NOT pass 5xx bodies through — it renders
          // Shopify's own HTML error page instead. So a server fault arrives
          // here as HTML, and res.json() would throw and be indistinguishable
          // from the customer being offline. Branch on content-type and tell
          // the truth about which one it was.
          var type = res.headers.get('content-type') || '';
          if (type.indexOf('application/json') === -1) {
            return { status: res.status, json: null, notJson: true };
          }
          return res.json().then(function (json) { return { status: res.status, json: json }; });
        })
        .then(function (r) {
          if (r.json && r.json.ok) {
            showSuccess(r.json);
            return;
          }
          if (r.notJson) {
            showError('Something went wrong on our end. Please try again shortly.');
            return;
          }
          showError((r.json && r.json.message) || 'Something went wrong. Please try again.');
        })
        .catch(function () {
          // A genuine transport failure — the request never completed. Distinct
          // from the server answering badly, and worth saying so rather than
          // blaming the address the customer typed.
          showError("We couldn't reach the server. Please check your connection and try again.");
        })
        .finally(function () {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = original;
        });
    });
  }

  function boot() {
    document.querySelectorAll('[data-tone-newsletter]').forEach(function (root) {
      init(root);

      // The inline footer script may already have flipped this root into its
      // completed state from the success cookie. That message is the generic
      // one, so re-attach the code if we still have it — otherwise a reload
      // silently downgrades "here is your code" to "check your inbox".
      if (root.getAttribute('data-newsletter-complete') === 'true') {
        const code = recallCode();
        const success = root.querySelector('[data-tone-newsletter-success]');
        if (code && success) {
          success.textContent =
            "You're on the list. Use code " + code + ' for 10% off your first bag.';
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
